import React, {useState, useEffect, Fragment} from 'react';
import {
  Button, Card, Intent, Tab, Tabs, Classes, Checkbox, FormGroup, InputGroup,
} from '@blueprintjs/core';
import {useMutation, useQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {useLocation} from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import queryString from 'query-string';

import {showWSErrors, showGQLError, showError} from 'utils/toaster';
import withAuthLayout from 'layouts/auth/view';

const Steps = {
  WELCOME: 0,
  CONTENT: 1,
  SHARE: 2,
  ANALYTICS: 3,
  PASSWORD: 4,
  APP: 5,
};

const FETCH_METADATA = gql`
  query AuthSetupFetchMetadata($shortcode: ID!) {
    inviteMetadata(shortcode: $shortcode) {
      clientName,
      privacyPolicyUrl,
      recipientName,
      recipientEmail,
    }
  }
`;

const ACTIVATE_ACCOUNT = gql`
  mutation AuthSetupAccountPageActivateAccount(
    $invitationShortcode: String!,
    $email: String!
    $fullName: String!,
    $password: String!,
  ) {
    activateAccount(
      invitationShortcode: $invitationShortcode,
      email: $email,
      fullName: $fullName,
      password: $password,
    ) {
      errors {
        title,
        message,
      },
    }
  }
`;

const AuthSetupAccountPage = () => {
  ////
  // Hooks
  //
  const location = useLocation();
  const {shortcode} = queryString.parse(location.search);
  const [step, setStep] = useState(Steps.WELCOME);
  const [inputs, setInputs] = useState({
    email: '',
    fullName: '',
    password: '',
    passwordConfirmation: '',
    agreedToWsPolicy: false,
    agreedToClientPolicy: false,
  });

  useEffect(() => {
    const [stepName] = Object.entries(Steps).find(([, stepId]) => stepId === step);
    mixpanel.track(`Open account setup step ${stepName}`);
  }, [step]);

  ////
  // GraphQL
  //
  const [activateAccount, {loading: loadingSubmit}] = useMutation(ACTIVATE_ACCOUNT, {
    onCompleted: ({activateAccount: {errors}}) => {
      if (errors.length > 0) {
        showWSErrors({errors});
      } else {
        setStep(Steps.APP);
      }
    },
    onError: error => showGQLError({error}),
  });

  const {loading: loadingMetadata, data: metadataResp} = useQuery(FETCH_METADATA, {
    variables: {shortcode},
    onError: error => showGQLError({error}),
    onCompleted: ({inviteMetadata}) => {
      setInputs({
        ...inputs,
        fullName: inviteMetadata.recipientName,
        email: inviteMetadata.recipientEmail,
      });
    },
  });

  ////
  // Events
  //
  const submit = (e) => {
    e.preventDefault();

    // Do some light client-side validation
    if (inputs.password.length < 8) {
      showError({message: 'Password must be at least 8 characters'});
      return;
    }

    if (inputs.password !== inputs.passwordConfirmation) {
      showError({message: 'Passwords do not match'});
      return;
    }

    if (inputs.fullName.length < 2) {
      showError({message: 'Full name must be greater than two characters'});
      return;
    }

    mixpanel.track('Submit account setup form');

    activateAccount({variables: {
      invitationShortcode: shortcode,
      email: inputs.email,
      fullName: inputs.fullName,
      password: inputs.password,
    }});
  };

  ////
  // Render
  //
  const loading = loadingMetadata || loadingSubmit;
  const showClientPolicy = !!metadataResp?.inviteMetadata?.privacyPolicyUrl;
  const agreedToPolicies = inputs.agreedToWsPolicy
    && (!showClientPolicy || inputs.agreedToClientPolicy);

  const renderWelcome = () => {
    return (
      <Card elevation={1} className="auth-setup--welcome">
        <div className="auth-setup--content">
          <div className="explainer">
            <h2>Welcome</h2>
            <p>
              Welcome to WorkSource, a simple yet powerful tool to manage,
              access, and distribute sales collateral. Before we begin, here
              are a few highlights of what you can look forward to.
            </p>
            <div className="actions">
              <Button
                className={Classes.MINIMAL}
                text="Skip tour"
                onClick={() => setStep(Steps.PASSWORD)}
                disabled={loading}
              />
              <Button
                intent={Intent.PRIMARY}
                text="Continue"
                disabled={loading}
                loading={loading}
                onClick={() => setStep(Steps.CONTENT)}
                icon="arrow-right"
              />
            </div>
          </div>
          <div className="preview-home" />
        </div>
      </Card>
    );
  };

  const renderContent = () => {
    return (
      <Card elevation={1} className="auth-setup--welcome">
        <div className="auth-setup--content">
          <div className="explainer">
            <h2>Your collateral, anytime; anywhere</h2>
            <p>
              With WorkSource you'll be able to access the latest sales
              collateral using our desktop website, or our mobile apps (on
              iPhone, iPad, and Android).
            </p>
            <p>
              Whenever a site admin adds or updates collateral, it will
              immediately appear on each of our apps, ensuring you're always up
              to date.
            </p>
            <div className="actions">
              <Button
                className={Classes.MINIMAL}
                text="Back"
                onClick={() => setStep(Steps.WELCOME)}
              />
              <Button
                intent={Intent.PRIMARY}
                text="Continue (1/3)"
                onClick={() => setStep(Steps.SHARE)}
                icon="arrow-right"
              />
            </div>
          </div>
          <div className="preview-files" />
        </div>
      </Card>
    );
  };

  const renderShare = () => {
    return (
      <Card elevation={1} className="auth-setup--welcome">
        <div className="auth-setup--content">
          <div className="explainer">
            <h2>Easily shared</h2>
            <p>
              We've made it simple and quick to get collateral into customers'
              hands. Our sharing feature allows you to easily send individual
              files or a set of files.
            </p>
            <p>
              Files can be sent via email, or by generating a custom link that
              can be sent through text, iMessage, WhatsApp, or any other app
              supported on your device.
            </p>
            <div className="actions">
              <Button
                className={Classes.MINIMAL}
                text="Back"
                onClick={() => setStep(Steps.CONTENT)}
              />
              <Button
                intent={Intent.PRIMARY}
                text="Continue (2/3)"
                onClick={() => setStep(Steps.ANALYTICS)}
                icon="arrow-right"
              />
            </div>
          </div>
          <div className="preview-share" />
        </div>
      </Card>
    );
  };

  const renderAnalytics = () => {
    return (
      <Card elevation={1} className="auth-setup--welcome">
        <div className="auth-setup--content">
          <div className="explainer">
            <h2>Analytics</h2>
            <p>
              Once shared, you can keep track of your customers' engagement
              with content. The "shares" section of the app will allow you to
              see when they've viewed or clicked on the files you've sent.
            </p>
            <div className="actions">
              <Button
                className={Classes.MINIMAL}
                text="Back"
                onClick={() => setStep(Steps.SHARE)}
              />
              <Button
                intent={Intent.PRIMARY}
                text="Setup account"
                onClick={() => setStep(Steps.PASSWORD)}
                icon="arrow-right"
              />
            </div>
          </div>
          <div className="preview-analytics" />
        </div>
      </Card>
    );
  };

  const renderPassword = () => {
    return (
      <Card elevation={1} className="auth-setup--welcome">
        <div className="auth-setup--content reverse">
          <div className="explainer">
            <h2>Get started</h2>
            <p>
              Now that you've gotten the basics, let's go ahead and set up your
              account by adding a password that you'll use to log into the app.
            </p>
          </div>
          <form className="setup-form" onSubmit={() => {}}>
            <FormGroup label="E-mail Address">
              <InputGroup
                disabled
                value={inputs.email}
                placeholder="user@example.com"
              />
            </FormGroup>
            <FormGroup label="Full Name">
              <InputGroup
                value={inputs.fullName}
                onChange={e => setInputs({...inputs, fullName: e.target.value})}
                placeholder="Janet Fogel"
              />
            </FormGroup>
            <FormGroup label="Password">
              <InputGroup
                value={inputs.password}
                disabled={loading}
                onChange={e => setInputs({...inputs, password: e.target.value})}
                type="password"
                placeholder="Secret"
              />
            </FormGroup>
            <FormGroup label="Password (confirm)">
              <InputGroup
                value={inputs.passwordConfirmation}
                onChange={e => setInputs({...inputs, passwordConfirmation: e.target.value})}
                disabled={loading}
                type="password"
                placeholder="Secret"
              />
            </FormGroup>

            <Checkbox
              label={(
                <Fragment>
                  I have read and agree to the{' '}
                  <a href="https://www.worksource.io/privacy" rel="noopener noreferrer" target="_blank">
                    WorkSource privacy policy
                  </a>.
                </Fragment>
              )}
              checked={inputs.agreedToWsPolicy}
              onChange={() => setInputs({...inputs, agreedToWsPolicy: !inputs.agreedToWsPolicy})}
            />
            {showClientPolicy && (
              <Checkbox
                label={(
                  <Fragment>
                    I have read and agree to the{' '}
                    <a href={metadataResp.inviteMetadata.privacyPolicyUrl} rel="noopener noreferrer" target="_blank">
                      {metadataResp.inviteMetadata.clientName} privacy policy
                    </a>.
                  </Fragment>
                )}
                checked={inputs.agreedToClientPolicy}
                onChange={() => {
                  setInputs({...inputs, agreedToClientPolicy: !inputs.agreedToClientPolicy});
                }}
              />
            )}
          </form>
        </div>
        <div className="actions">
          <Button
            className={Classes.MINIMAL}
            text="Back"
            onClick={() => setStep(Steps.ANALYTICS)}
          />
          <Button
            intent={Intent.PRIMARY}
            text="Finish"
            onClick={submit}
            disabled={!agreedToPolicies}
            icon="arrow-right"
          />
        </div>
      </Card>
    );
  };

  const renderApp = () => {
    const ios = (
      <div className="download">
        Go to the following URL on your device:
        <a rel="noopener noreferrer" target="_blank" className="url" href="https://www.worksource.io/ios">
          https://www.worksource.io/ios
        </a>
      </div>
    );

    const android = (
      <div className="download">
        Go to the following URL on your device:
        <a rel="noopener noreferrer" target="_blank" className="url" href="https://www.worksource.io/and">
          https://www.worksource.io/and
        </a>
      </div>
    );

    return (
      <Card elevation={1} className="auth-setup--welcome">
        <div className="auth-setup--content reverse">
          <div className="explainer">
            <h2>Download the App</h2>
            <p>
              Last thing! Go ahead and download the app on your device in order
              to start viewing and sharing your collateral.
            </p>
            <p>
              We hope you enjoy WorkSource. If you have any difficulties or
              need assistance, please don't hesitate to reach out via the
              feedback button in the app.
            </p>
            <p>
              Thanks for signing up!
            </p>
          </div>
          <div className="app-downloads">
            <Card>
              <Tabs defaultSelectedTabId="ios" id="platform-selector">
                <Tab
                  id="ios"
                  title="iPhone/iPad"
                  panel={ios}
                />
                <Tab
                  id="android"
                  title="Android"
                  panel={android}
                />
              </Tabs>
            </Card>
          </div>
        </div>
        <div className="actions">
          <Button
            intent={Intent.PRIMARY}
            className={[Classes.LARGE, 'auth-setup--sign-in']}
            text="Sign into the desktop app"
            onClick={() => {
              window.location = process.env.UI_URL;
            }}
            icon="arrow-right"
          />
        </div>
      </Card>
    );
  };

  return {
    [Steps.WELCOME]: renderWelcome,
    [Steps.CONTENT]: renderContent,
    [Steps.SHARE]: renderShare,
    [Steps.ANALYTICS]: renderAnalytics,
    [Steps.PASSWORD]: renderPassword,
    [Steps.APP]: renderApp,
  }[step]();
};

export default withAuthLayout()(AuthSetupAccountPage);
